import React, { useCallback, useState } from "react";
import { IconButton } from "@mui/material";
import { StyledIcon } from "../../../components/StyledIcon/StyledIcon";
import { CustomButtonVariant } from "../../../components/CustomButton/CustomButton";
import { IFinancialService } from "../../../modules/financial/models/IFinancialService";
import { CategoryType } from "../../../modules/financial/models/GetAllFinancialDTO";
import useDialogAlert from "../../../hooks/DialogAlert";
import { downloadOpenWindow } from "../../../utils/download";
import DetailsModal from "../../../components/ModalDetailsGeneric/ModalDetailsGeneric";
import { StatementRow } from "../models/StatementRows";

interface ActionButtonsTableProps {
  index: number;
  row: StatementRow;
  financialService: IFinancialService;
}

const ActionButtonsTable: React.FC<ActionButtonsTableProps> = ({
  row,
  financialService,
}) => {
  const [open, setOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { snackbar } = useDialogAlert();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const downloadPaymentSlip = useCallback(async () => {
    try {
      setIsDownloading(true);
      const response = await financialService.downloadPaymentSlip({
        atemId: row.atemId,
        invoiceId: row.invoiceId,
        installmentNumber: row.installmentNumber,
        year: row.year,
      });
      downloadOpenWindow(response, "pdf");
    } catch (error) {
      snackbar({
        message: "Erro ao fazer download do boleto.",
        variant: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  }, [row, financialService, snackbar]);

  const downloadNotes = useCallback(async () => {
    const typeService =
      row.category === "CreditNote"
        ? "CREDIT"
        : row.category === "DebitNote"
        ? "DEBIT"
        : undefined;

    if (!typeService) return;

    try {
      setIsDownloading(true);
      const response = await financialService.downloadCreditNoteOrDebitNote({
        service: typeService,
        customerNumber: row.customerNumber,
        documentNumber: row.invoiceId,
        companyCode: row.companyCode,
        fiscalYear: row.fiscalYear,
        documentItem: row.documentItem,
      });
      downloadOpenWindow(response, "pdf");
    } catch (error) {
      snackbar({
        message: "Erro ao fazer download da nota.",
        variant: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  }, [row, financialService, snackbar]);

  const isDownloadDisabled = (item: StatementRow): boolean => {
    const isCash = item.category === CategoryType.Cash;
    const isDeposit =
      item.category === CategoryType.DeferredPayment &&
      item.paymentMethod === "Depósito Bancário";
    const isBoletoMissingInfo =
      item.category === CategoryType.DeferredPayment &&
      item.paymentMethod === "Boleto a Receber" &&
      (!item.companyBank || !item.ourNumber);

    return isCash || isDeposit || isBoletoMissingInfo;
  };

  const isDebitNote = row.category === "DebitNote";
  const isCreditNote = row.category === "CreditNote";

  const getDownloadAction = () => {
    if (isDebitNote) {
      return {
        label: "Baixar Nota de Débito",
        variant: isDownloading
          ? CustomButtonVariant.OUTLINED_DOWNLOAD_LOADING
          : CustomButtonVariant.OUTLINED_DOWNLOAD,
        onClick: downloadNotes,
      };
    }
    if (isCreditNote) {
      return {
        label: "Baixar Nota de Crédito",
        variant: isDownloading
          ? CustomButtonVariant.OUTLINED_DOWNLOAD_LOADING
          : CustomButtonVariant.OUTLINED_DOWNLOAD,
        onClick: downloadNotes,
      };
    }
    // Para demais categorias (DeferredPayment, Cash, etc.), exibe "Baixar Boleto".
    // Quando isDownloadDisabled retorna true, o botão fica com variante DISABLED (não é omitido).
    return {
      label: "Baixar Boleto",
      variant: isDownloadDisabled(row)
        ? CustomButtonVariant.DISABLED
        : isDownloading
        ? CustomButtonVariant.OUTLINED_DOWNLOAD_LOADING
        : CustomButtonVariant.OUTLINED_DOWNLOAD,
      onClick: isDownloadDisabled(row) ? undefined : downloadPaymentSlip,
    };
  };

  const downloadAction = getDownloadAction();

  const modalActions = [
    downloadAction,
    {
      label: "Fechar",
      variant: CustomButtonVariant.CONTAINED,
      onClick: handleClose,
    },
  ];

  return (
    <>
      <IconButton onClick={handleOpen}>
        <StyledIcon iconType="info" />
      </IconButton>

      <DetailsModal
        open={open}
        onClose={handleClose}
        title="Detalhes do Extrato"
        header={{
          title: row.customerName,
        }}
        data={[
          { label: "Documento", value: row.document },
          { label: "Categoria", value: row.categoryTranslation },
          { label: "Valor", value: row.value },
          { label: "Data de compensação", value: row.compensationDate || "-" },
          {
            label: "Forma de Pagamento",
            value: row.paymentMethod,
          },
          {
            label: "Chave de Acesso",
            value: row.accessKey || "-",
          },
          { label: "Banco", value: row.companyBank || "-" },
          { label: "Nosso Número", value: row.ourNumber || "-" },
          {
            label: "Descrição",
            value: row.description || "-",
            fullWidth: true,
          },
        ]}
        actions={modalActions}
      />
    </>
  );
};

export default ActionButtonsTable;
